import { GridList, EmptyState } from '~publish/legacy/shared-components'
import {
  ExternalLinkIcon,
  toast,
  Tooltip,
  Button,
  CopyIcon,
  Separator,
  Text,
  Heading,
  Skeleton,
} from '@buffer-mono/popcorn'

import getErrorBoundary from '~publish/legacy/web/components/ErrorBoundary'

import ProfilesDisconnectedBanner from '~publish/legacy/profiles-disconnected-banner'
import { getChannelProperties, openPreviewPage } from '../../util'
import CustomLinks from '../CustomLinks'
import styles from './index.module.css'
import {
  BufferTrackerReact as BufferTracker,
  BufferTrackerReact,
} from '@buffer-mono/tracking-plan'
import type {
  CustomLinksDetails,
  Profile,
} from '~publish/legacy/profile-sidebar/types'
import { Views } from '~publish/pages/Channel/ChannelShopGrid/ChannelShopGrid'
import { usePaginatedPostList } from '~publish/pages/AllChannels/PostList/usePaginatedPostList'
import { getFragmentData } from '~publish/gql/fragment-masking'
import { PostCard_Post } from '~publish/components/PostCard/PostCard'
import { ensureUrlProtocol } from '~publish/legacy/composer/composer/utils/StringUtils'
import {
  useAddNewCustomLink,
  useDeleteCustomLink,
  useEditCustomLink,
  useSwapCustomLinks,
  useUpdateCustomLinkColors,
  useUpdatePostLink,
} from '../../queries/hooks'

const ErrorBoundary = getErrorBoundary(true)

export interface GridPostsProps {
  profile: Profile
  maxCustomLinks?: number
  selectedView: Views
  customLinksDetails: CustomLinksDetails
}

const onCopyToClipboard = ({
  publicGridUrl,
  profile,
}: {
  publicGridUrl: string
  profile?: Partial<Profile>
}): void => {
  navigator.clipboard.writeText(publicGridUrl)

  BufferTracker.shopGridPageLinkCopied({
    ...getChannelProperties(profile),
    shopGridUrl: publicGridUrl || '',
  })

  toast.success('Copied!')
}

const onPreviewClick = (publicGridUrl: string): void => {
  openPreviewPage(publicGridUrl)
}

const GridPreview = ({
  profile,
  maxCustomLinks = 3,
  selectedView,
  customLinksDetails = {
    links: [],
    color: null,
    contrastColor: null,
  },
}: GridPostsProps): JSX.Element => {
  const isDisconnectedProfile = profile?.isDisconnected
  const publicGridUrl = `https://shopgr.id/${profile?.serviceUsername}`
  const previewGridUrl = `https://buffer.com/p/${profile?.serviceUsername}`
  const hasWriteAccess = !!profile?.permissions?.includes?.('buffer_write')

  const channelId = profile?.id || ''

  const { data, loading, refetch } = usePaginatedPostList({
    channelIds: [channelId],
    status: ['scheduled', 'sent'],
    sortPreset: 'mostRecentlyPostedFirst',
    perPage: 100,
  })
  const [updateCustomLinkColors] = useUpdateCustomLinkColors({
    channelId,
  })
  const [addNewCustomLink] = useAddNewCustomLink({
    channelId,
  })
  const [deleteCustomLink] = useDeleteCustomLink({
    channelId,
  })
  const [editCustomLink] = useEditCustomLink({
    channelId,
  })
  const [swapCustomLinks] = useSwapCustomLinks({
    channelId,
  })
  const [updatePostLink] = useUpdatePostLink({
    refetch,
    profile,
  })

  const flattenedPostEdges = !data?.posts.edges?.length
    ? []
    : data.posts.edges.map((edge) => edge.node)
  const posts = getFragmentData(PostCard_Post, flattenedPostEdges)

  const gridPosts = posts.filter(
    (post) =>
      ['reel', 'post'].includes(post.metadata?.type || '') &&
      post.notificationStatus == null,
  )

  if (loading) {
    return (
      <div className={styles.loadingGridContainer}>
        {new Array(Math.ceil(window.innerHeight / 410) * 3)
          .fill(null)
          .map((_, index) => (
            <Skeleton key={index} height={410} width={308} />
          ))}
      </div>
    )
  }

  return (
    <ErrorBoundary>
      {isDisconnectedProfile ? (
        <ProfilesDisconnectedBanner />
      ) : (
        <div>
          <div className={styles.header}>
            {selectedView === Views.ShopGrid && (
              <>
                <Heading size="medium" className={styles.title}>
                  Shop Grid
                </Heading>

                <Heading size="small">Public Page URL</Heading>

                <div className={styles.buttonWrapper}>
                  <div className={styles.linkField}>
                    <Tooltip arrow content="Copy Page Link">
                      <button
                        className={styles.copyLinkButton}
                        type="button"
                        onClick={(): void => {
                          onCopyToClipboard({
                            publicGridUrl,
                            profile,
                          })
                        }}
                      >
                        {publicGridUrl}
                        <CopyIcon size="small" />
                      </button>
                    </Tooltip>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={(): void => {
                      onPreviewClick(previewGridUrl)

                      BufferTrackerReact.shopGridPagePreviewed(
                        getChannelProperties(profile),
                      )
                    }}
                  >
                    <ExternalLinkIcon />
                    Preview Page
                  </Button>
                </div>
              </>
            )}
          </div>
          {selectedView === Views.ShopGrid && (
            <>
              <Separator />
              <CustomLinks
                customLinksDetails={customLinksDetails}
                onUpdateCustomLinksColor={(colors): void => {
                  updateCustomLinkColors({
                    variables: {
                      input: {
                        channelId,
                        color: colors.color,
                        contrastColor: colors.contrastColor,
                      },
                    },
                  })
                }}
                onDeleteCustomLink={(customLinkId): void => {
                  deleteCustomLink({
                    variables: {
                      input: {
                        channelId,
                        id: customLinkId,
                      },
                    },
                  })
                }}
                maxCustomLinks={maxCustomLinks}
                onSwapCustomLinks={(
                  customLinkSource,
                  customLinkTarget,
                ): void => {
                  swapCustomLinks({
                    variables: {
                      input: {
                        channelId,
                        sourceId: customLinkSource.id,
                        targetId: customLinkTarget.id,
                      },
                    },
                  })
                }}
                onSaveNewLinkClick={(item): void => {
                  addNewCustomLink({
                    variables: {
                      input: {
                        channelId,
                        text: item.text,
                        url: ensureUrlProtocol(item.url),
                      },
                    },
                  })
                }}
                onUpdateSingleCustomLink={(item): void => {
                  editCustomLink({
                    variables: {
                      input: {
                        channelId,
                        id: item.id,
                        text: item.text,
                        url: ensureUrlProtocol(item.url),
                      },
                    },
                  })
                }}
                hasWriteAccess={hasWriteAccess}
              />
              <Separator />
              <div className={styles.titleWrapper}>
                <Heading size="small">Post Links</Heading>
                <Text as="p" color="subtle">
                  Add links to your posts. Only posts with links will appear on
                  your public Shop Grid page.
                </Text>
              </div>
            </>
          )}
          {gridPosts.length < 1 && (
            <EmptyState
              title="No posts published yet!"
              subtitle="Once you've posted to your instagram account, your posts will show up here."
              heroImg="https://s3.amazonaws.com/buffer-publish/images/empty-sent2x.png"
              heroImgSize={{ width: '270px', height: '150px' }}
            />
          )}
          {gridPosts.length >= 1 && (
            <GridList
              gridPosts={gridPosts}
              hasWriteAccess={hasWriteAccess}
              timezone={profile?.timezone}
              showExtraContent={selectedView === Views.ShopGrid}
              onSavePostUrl={(post, link): void => {
                updatePostLink({
                  variables: {
                    input: {
                      postId: post.id,
                      link: link ? ensureUrlProtocol(link) : null,
                      channelId,
                    },
                  },
                })
              }}
            />
          )}
        </div>
      )}
    </ErrorBoundary>
  )
}

export default GridPreview
